import React, { useEffect, useState, useRef } from "react";
import { renderError } from "../../../helpers/errors";
import { createFilterQueryString } from "../../../helpers/helpers";
import { convertIntToMoney } from "../../../helpers/conversions";
import api from "../../../services/api";
import InputSelect from "../../InputSelect";

const SEARCH_DEBOUNCE_MS = 300;
const DEFAULT_RESULTS_PER_PAGE = 20;
const MIN_SEARCH = 1;

const toOption = budget => ({
    value: budget.id,
    label: [
        budget.code,
        budget.company_trade_name ?? budget.customer_name,
        convertIntToMoney(budget.total ?? 0, true),
    ].filter(Boolean).join(' - '),
});

// Busca de orçamentos por código, nome da pessoa ou nome da empresa
// (grupos de filtro em OR). Usa /budget/paginate, que já devolve o shape
// enxuto da listagem (code, customer_name, company_trade_name, total).
export const SelectBudget = ({
    label = 'Orçamento',
    selectedBudget,
    setSelectedBudget,
    perPage = DEFAULT_RESULTS_PER_PAGE,
}) => {
    const [options, setOptions] = useState([]);
    const [selectedOption, setSelectedOption] = useState(null);
    const [search, setSearch] = useState('');
    const [noOptionsText, setNoOptionsText] = useState(null);
    const debounceRef = useRef(null);
    const abortRef = useRef(null);

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (search.length < MIN_SEARCH) {
            setNoOptionsText(`Digite o código, a pessoa ou a empresa...`);
            setOptions([]);
            if (abortRef.current) abortRef.current.abort();
            return;
        }
        debounceRef.current = setTimeout(() => getBudgets(), SEARCH_DEBOUNCE_MS);
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [search]);

    const getBudgets = () => {
        setOptions([]);
        setNoOptionsText('Carregando...');
        if (abortRef.current) abortRef.current.abort();
        abortRef.current = new AbortController();

        const stringFilters = createFilterQueryString([
            [`[code][equal]=${search}`],
            [`[related][customer.name][contain]=${search}`],
            [`[related][company.trade_name][contain]=${search}`],
        ]);

        api.get(`/budget/paginate?${stringFilters}per_page=${perPage}`, {
            signal: abortRef.current.signal,
        }).then(res => {
            const raw = res.data?.resources?.data ?? res.data?.resources ?? [];
            const list = Array.isArray(raw) ? raw : [];
            setOptions(list.map(toOption));
            setNoOptionsText(list.length === 0 ? 'Nenhum orçamento encontrado' : null);
        }).catch(error => {
            if (error.code === 'ERR_CANCELED') return;
            renderError(error);
            setNoOptionsText('Erro ao carregar. Tente novamente.');
        });
    }

    const handleChange = value => {
        setSelectedOption(options.find(option => option.value === value) ?? null);
        setSelectedBudget(value);
    }

    // Mantém a opção escolhida visível mesmo quando uma nova busca troca a lista.
    const visibleOptions = selectedOption && !options.some(option => option.value === selectedOption.value)
        ? [selectedOption, ...options]
        : options;

    return (
        <InputSelect
            noOptionsText={noOptionsText}
            label={label}
            options={visibleOptions}
            value={selectedBudget}
            change={handleChange}
            setSearch={value => setSearch(value)}
            filterOption={true}
        />
    );
}

export default SelectBudget;
